import React, {useState} from "react";
import {Archive, Monitor, MonitorSmartphone, Package, Plug, Printer, ScanBarcode, Tablet} from "lucide-react";
import {discountPercent, effectivePrice, formatMoney, hasDiscount} from "./marketplaceApi";

// Small pieces shared by the Marketplace tabs.

export const CATEGORY_ICONS = {
    pos: Monitor,
    printers: Printer,
    drawers: Archive,
    scanners: ScanBarcode,
    tablets: Tablet,
    stands: MonitorSmartphone,
    apple: Plug,
    supplies: Package,
};

export const iconFor = (category) => CATEGORY_ICONS[category] || Package;

// True while the product photo can be shown (set, and not failed to load)
const usePhoto = (product) => {
    const [failed, setFailed] = useState(false);
    return [Boolean(product.image) && !failed, () => setFailed(true)];
};

// Product photo, or the category icon on a teal tile (same size as catalogue avatars)
export const ProductThumb = ({product, size = "md"}) => {
    const box = size === "lg" ? "h-16 w-16" : size === "sm" ? "h-9 w-9" : "h-12 w-12";
    const Icon = iconFor(product.category);
    const [showPhoto, onError] = usePhoto(product);
    return showPhoto ? (
        <img src={product.image} alt={product.name} onError={onError}
             className={`${box} flex-shrink-0 rounded-lg object-contain bg-white border border-gray-200 dark:border-gray-700`}/>
    ) : (
        <div className={`${box} flex-shrink-0 flex items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20`}>
            <Icon className={size === "lg" ? "w-8 h-8 text-primary" : size === "sm" ? "w-4 h-4 text-primary" : "w-6 h-6 text-primary"} strokeWidth={1.5}/>
        </div>
    );
};

// Large visual of a grid card / detail popup: product photo, or the category
// icon in a white disc on a soft teal gradient.
export const ProductCover = ({product, className = "h-44"}) => {
    const Icon = iconFor(product.category);
    const [showPhoto, onError] = usePhoto(product);
    return showPhoto ? (
        <div className={`${className} w-full flex items-center justify-center bg-white overflow-hidden`}>
            <img src={product.image} alt={product.name} onError={onError}
                 className="max-h-full max-w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"/>
        </div>
    ) : (
        <div className={`${className} w-full flex items-center justify-center bg-gradient-to-br from-primary-lighter via-white to-primary/20 dark:from-primary/25 dark:via-gray-800 dark:to-primary/10`}>
            <div className="h-20 w-20 rounded-full bg-white/90 dark:bg-gray-900/70 shadow-sm flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <Icon className="w-10 h-10 text-primary-dark dark:text-primary" strokeWidth={1.25}/>
            </div>
        </div>
    );
};

export const StockBadge = ({inStock}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        inStock
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
    }`}>
        {inStock ? "En stock" : "Sur commande"}
    </span>
);

// Compact availability: coloured dot + label, for cards
export const StockDot = ({inStock}) => (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
        <span className={`h-2 w-2 rounded-full ${inStock ? "bg-green-500" : "bg-amber-500"}`}/>
        {inStock ? "En stock" : "Sur commande"}
    </span>
);

export const DiscountBadge = ({product}) => hasDiscount(product) ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500 text-white shadow-sm">
        -{discountPercent(product)}%
    </span>
) : null;

// Price paid, with the crossed-out list price when a remise applies
export const PriceCell = ({product}) => (
    <div className="whitespace-nowrap">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(effectivePrice(product))}</div>
        {hasDiscount(product) && (
            <div className="text-xs text-gray-400 line-through">{formatMoney(product.price)}</div>
        )}
    </div>
);

// Bigger price block of the grid cards and detail popup
export const PriceTag = ({product, large = false}) => (
    <div>
        <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`font-semibold text-gray-900 dark:text-white ${large ? "text-2xl" : "text-lg"}`}>
                {formatMoney(effectivePrice(product))}
            </span>
            {hasDiscount(product) && (
                <span className="text-sm text-gray-400 line-through">{formatMoney(product.price)}</span>
            )}
        </div>
        {hasDiscount(product) && (
            <div className="text-xs font-medium text-red-600 dark:text-red-400">
                Économisez {formatMoney(product.price - product.discountPrice)}
            </div>
        )}
    </div>
);

// Liste / Grille switch, same height and border as the toolbar fields
export const ViewToggle = ({value, onChange, options}) => (
    <div role="radiogroup" aria-label="Affichage"
         className="flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
        {options.map(({key, label, Icon}) => (
            <button
                key={key}
                type="button"
                role="radio"
                aria-checked={value === key}
                title={label}
                onClick={() => onChange(key)}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    value === key
                        ? "bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
            >
                <Icon className="w-5 h-5" strokeWidth={1.75}/>
                <span className="sm:hidden xl:inline">{label}</span>
            </button>
        ))}
    </div>
);

// Category chips with icon + count; key "" = toutes
export const CategoryChips = ({categories, value, onChange, counts, total}) => {
    const chip = (active) =>
        `inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-full text-sm whitespace-nowrap border transition-colors ${
            active
                ? "bg-primary text-white border-primary shadow-sm shadow-primary/30"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:text-primary-dark dark:hover:text-primary"
        }`;
    const count = (active, n) => (
        <span className={`min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs flex items-center justify-center ${
            active ? "bg-white/25 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
        }`}>{n}</span>
    );
    return (
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <button type="button" className={chip(!value)} onClick={() => onChange("")}>
                Tout {count(!value, total)}
            </button>
            {categories.map((c) => {
                const Icon = iconFor(c.key);
                const active = value === c.key;
                return (
                    <button key={c.key} type="button" className={chip(active)} onClick={() => onChange(c.key)}>
                        <Icon className="w-4 h-4" strokeWidth={1.75}/>
                        {c.label}
                        {count(active, counts[c.key] || 0)}
                    </button>
                );
            })}
        </div>
    );
};
